import axios from "axios";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import {
  authUrls,
  authValidationConstants,
  identityApiBaseUrl,
} from "../../constants";

import {
  useHookForm,
  useMerchantApiMutation,
  useQueryString,
} from "../../hooks";
import {
  IHookForm,
  IRegisterRequest,
  IRegisterResponse,
  IResendConfirmationEmailRequest,
} from "../../interfaces-and-types";
import { globalHandlerUtils } from "../../utils";
import { View } from "./View";

export interface ISignupViewProps extends IHookForm<IRegisterRequest> {
  formValues: IRegisterRequest;
  isPasswordTouched: boolean;
  signUpSuccessful: boolean;
  isLoadingResendConfirmationEmail: boolean;
  redirectToLogin: () => void;
  targetPageOnLogin: string;
  resendEmailConfirmationLink: () => void;
}

export const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const target_page = useQueryString().get("target_page");

  const {
    // isLoading: isLoadingRegister,
    // isError: isErrorRegister,
    // data: apiResponseRegister,
    isSuccess: isSuccessRegister,
    mutate: signUp,
  } = useMerchantApiMutation<IRegisterRequest, IRegisterResponse>(
    "register"
  ).usePostMutation;

  const {
    isLoading: isLoadingResendConfirmationEmail,
    // isSuccess: isSuccessResendConfirmationEmail,
    mutate: resendConfirmationEmail,
  } = useMerchantApiMutation<IResendConfirmationEmailRequest, null>(
    "resendConfirmationEmail"
  ).usePostMutation;

  const resendEmailConfirmationLink = () =>
    resendConfirmationEmail({ email: formValues.email });

  useEffect(() => {
    if (isSuccessRegister) {
      reset({
        email: formValues.email,
      }); // display success message and request email confirmation
    }
  }, [isSuccessRegister]);

  const initialFormValues: IRegisterRequest = {
    invitation_code: "",

    email: "",
    password: "",
    confirm_password: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    merchant_name: "",
    // businessType: "",
    country: "NG",
    is_consent_checked: false,
  };

  const signUpSchema: yup.SchemaOf<IRegisterRequest> = yup
    .object({
      invitation_code: yup
        .string()
        .trim()
        .required("Invitation code is required"),

      email: yup
        .string()
        .trim()
        .email()
        .required("Email is required")
        .test(
          "email", // Name
          "Email is already taken", // Msg
          async (email, testContext) => {
            try {
              const {
                data: { is_valid, error_message },
              } = await axios.post(
                identityApiBaseUrl + authUrls.remoteValidateEmail,
                { email }
              );

              if (!is_valid)
                return testContext.createError({ message: error_message });

              return is_valid;
            } catch (error) {
              // console.log("throwing email network error logs");
              testContext.createError({ message: "No network" });
            }
          }
        ),
      password: yup
        .string()
        .required("Password is required")
        .min(
          authValidationConstants.minPasswordLength,
          ({ min }) => `Password must be at least ${min} characters`
        )
        .max(
          authValidationConstants.maxPasswordLength,
          ({ max }) => `Password must have a max of ${max} characters`
        )
        .matches(/\w*[a-z]\w*/, "Password must have a small letter")
        .matches(/\w*[A-Z]\w*/, "Password must have a capital letter")
        .matches(/\d/, "Password must have a number")
        .matches(
          /[!@#$%^&*()\-_"=+{};]/,
          `Password must have a special character`
        )
        .test(
          "password", // Name
          () => errors.password?.message, // Msg
          async (password, testContext) => {
            try {
              const {
                data: { is_valid, error_message },
              } = await axios.post(
                identityApiBaseUrl + authUrls.remoteValidatePassword,
                { password }
              );

              if (!is_valid)
                return testContext.createError({ message: error_message });

              return is_valid;
            } catch (error) {
              // console.log("throwing password network error logs");
              testContext.createError({ message: "No network" });
            }
          }
        ),
      confirm_password: yup
        .string()
        .oneOf(
          [yup.ref("password"), null],
          "Password and confirmation must match"
        )
        .required("Password confirmation is required"),
      first_name: yup.string().required("First Name is required"),
      last_name: yup.string().required("Last Name is required"),
      phone_number: yup.string().required("Phone Number is required"),
      country: yup.string().required("Country is required"),
      merchant_name: yup.string().required("Merchant Name is required"),
      // businessType: yup.string().required("Business Type is required"),
      is_consent_checked: yup
        .boolean()
        .required()
        .isTrue("You must agree to the terms and conditions"),
    })
    .required();

  const handleApiSubmit = (data: IRegisterRequest) => {
    signUp(data);
  };

  const targetPageOnLogin = `/login${location.search}`;
  const redirectToLogin = () => {
    navigate(targetPageOnLogin);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset, trigger, watch } = useHookForm(
    {
      initialFormValues,
      validationSchema: signUpSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
      mode: "all",
    }
  );

  const formValues = watch();
  const isPasswordTouched = !!formValues.password;

  useEffect(() => {
    trigger("email"); // trigger validation on load to check email availability
  }, [formValues.email]);

  return (
    <View
      signUpSuccessful={isSuccessRegister}
      redirectToLogin={redirectToLogin}
      targetPageOnLogin={targetPageOnLogin}
      isLoadingResendConfirmationEmail={isLoadingResendConfirmationEmail}
      resendEmailConfirmationLink={resendEmailConfirmationLink}
      formValues={formValues}
      isPasswordTouched={isPasswordTouched}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
